import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Chip, Alert, Snackbar } from '@mui/material';
import { AutoFixHigh as EnhanceIcon } from '@mui/icons-material';
import useImageTransform from '../../../../hooks/useImageGen';
import { useSubscription } from '../../../../contexts/SubscriptionContext';
import { AnalysisResult, EnhancementSettings, SelectedPhoto } from '../types';
import StepperBar from './StepperBar';
import StepAnalyze from './StepAnalyze';
import StepReview from './StepReview';
import StepEnhance from './StepEnhance';
import StepSave from './StepSave';

interface TransformationPanelProps {
   selectedPhotos: SelectedPhoto[];
   onSavePhoto: (photo: {
      id: string;
      originalPhotoId: string;
      transformedUrl: string;
      type: string;
      tripId?: string;
      eventId?: string;
      userId?: string;
   }) => Promise<void>;
}

const TransformationPanel: React.FC<TransformationPanelProps> = ({
   selectedPhotos = [],
   onSavePhoto,
}) => {
   const { analyzeImage, enhanceImage } = useImageTransform();
   const { checkSubscriptionStatus } = useSubscription();

   const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(1);
   const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
   const [settings, setSettings] = useState<EnhancementSettings>({
      brightness: 10, contrast: 15, sharpness: 15, saturation: 10,
      warmth: 5, shadows: 10, skinSmoothing: 0,
   });
   const [recommendedSettings, setRecommendedSettings] = useState<EnhancementSettings | null>(null);
   const [enhancedImageUrl, setEnhancedImageUrl] = useState<string | null>(null);
   const [enhancedImageId, setEnhancedImageId] = useState<string | null>(null);
   const [isAnalyzing, setIsAnalyzing] = useState(false);
   const [isEnhancing, setIsEnhancing] = useState(false);

   const [snackMsg, setSnackMsg] = useState('');
   const [snackSeverity, setSnackSeverity] = useState<'error' | 'success'>('error');
   const [snackOpen, setSnackOpen] = useState(false);

   const prevPhotoRef = useRef<string | null>(null);
   const selectedPhoto = selectedPhotos[0] || null;

   const isModified = useMemo(() => {
      if (!recommendedSettings) return false;
      return (Object.keys(recommendedSettings) as (keyof EnhancementSettings)[]).some(
         (k) => settings[k] !== recommendedSettings[k]
      );
   }, [settings, recommendedSettings]);

   // Auto-advance to Step 2 when photo selected
   useEffect(() => {
      if (!selectedPhoto) {
         setCurrentStep(1);
         return;
      }

      if (prevPhotoRef.current === selectedPhoto.url) return;
      prevPhotoRef.current = selectedPhoto.url;

      setAnalysis(null);
      setEnhancedImageUrl(null);
      setEnhancedImageId(null);
      setCurrentStep(2);
      runAnalysis(selectedPhoto);
   }, [selectedPhoto]);

   const runAnalysis = useCallback(async (photo: SelectedPhoto) => {
      try {
         setIsAnalyzing(true);
         const result = await analyzeImage({
            url: photo.url,
            sourceType: photo.type || 'general',
         });
         setAnalysis(result);
         setSettings({ ...result.recommended });
         setRecommendedSettings({ ...result.recommended });
         setIsAnalyzing(false);

         // Brief delay so checklist animation finishes
         setTimeout(() => setCurrentStep(3), 600);
      } catch (err: any) {
         setIsAnalyzing(false);
         setSnackSeverity('error');
         setSnackMsg(err.message || 'Analysis failed');
         setSnackOpen(true);
         setCurrentStep(1);
      }
   }, [analyzeImage]);

   const handleEnhance = useCallback(async () => {
      if (!selectedPhoto) return;

      if (checkSubscriptionStatus && !checkSubscriptionStatus()) return;

      try {
         setCurrentStep(4);
         setIsEnhancing(true);

         const result = await enhanceImage({
            url: selectedPhoto.url,
            preset: 'custom',
            settings,
         });

         const img = result.images?.[0];
         if (img) {
            setEnhancedImageUrl(img.url);
            setEnhancedImageId(img.image_id);
         }

         setIsEnhancing(false);
         setTimeout(() => setCurrentStep(5), 400);
      } catch (err: any) {
         setIsEnhancing(false);
         setSnackSeverity('error');
         setSnackMsg(err.message || 'Enhancement failed');
         setSnackOpen(true);
         setCurrentStep(3);
      }
   }, [selectedPhoto, settings, enhanceImage, checkSubscriptionStatus]);

   const handleSettingsChange = (key: keyof EnhancementSettings, value: number) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
   };

   const handleReset = () => {
      if (recommendedSettings) setSettings({ ...recommendedSettings });
   };

   const handleBack = () => {
      setCurrentStep(1);
      setAnalysis(null);
      setEnhancedImageUrl(null);
      prevPhotoRef.current = null;
   };

   const handleReEnhance = () => {
      setEnhancedImageUrl(null);
      setEnhancedImageId(null);
      setCurrentStep(3);
   };

   const handleNewPhoto = () => {
      prevPhotoRef.current = null;
      setCurrentStep(1);
      setAnalysis(null);
      setEnhancedImageUrl(null);
      setEnhancedImageId(null);
   };

   const handleSave = async () => {
      if (!selectedPhoto || !enhancedImageUrl || !enhancedImageId) return;
      await onSavePhoto({
         id: enhancedImageId,
         originalPhotoId: selectedPhoto.id,
         transformedUrl: enhancedImageUrl,
         type: selectedPhoto.type,
         tripId: selectedPhoto.tripId,
         eventId: selectedPhoto.eventId,
         userId: selectedPhoto.userId,
      });
   };

   const sourceLabel = selectedPhoto?.type === 'event' ? 'EVENT'
      : selectedPhoto?.type === 'trip' ? 'TRIP' : 'PROFILE';

   return (
      <Paper sx={{
         p: { xs: 2, sm: 3 },
         background: '#111815',
         border: '1px solid #1F2E2A',
         borderRadius: '12px',
      }}>
         <StepperBar currentStep={currentStep} />

         {/* Step 1: Select */}
         {currentStep === 1 && (
            selectedPhoto ? (
               <Box>
                  <Box sx={{
                     width: '100%',
                     height: { xs: 260, sm: 340 },
                     overflow: 'hidden',
                     borderRadius: '8px',
                     border: '1px solid #253530',
                     mb: 2,
                     background: '#0A0F0C',
                  }}>
                     <img
                        src={selectedPhoto.url}
                        alt={selectedPhoto.title || 'Selected photo'}
                        style={{
                           width: '100%', height: '100%',
                           objectFit: 'contain', imageOrientation: 'from-image',
                        }}
                     />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                     <Chip
                        label={sourceLabel}
                        size="small"
                        sx={{
                           background: 'rgba(29,158,117,0.12)',
                           color: '#1D9E75',
                           border: '1px solid rgba(29,158,117,0.3)',
                           fontSize: '11px', fontWeight: 600,
                        }}
                     />
                     {selectedPhoto.title && (
                        <Typography sx={{ fontSize: '13px', color: '#B0BEC5' }}>
                           {selectedPhoto.title}
                        </Typography>
                     )}
                  </Box>
               </Box>
            ) : (
               <Box sx={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  py: 8, gap: 1.5,
               }}>
                  <EnhanceIcon sx={{ fontSize: 48, color: '#253530' }} />
                  <Typography variant="body1" sx={{ color: '#546E7A' }}>
                     Select a photo to enhance
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#37474F', fontSize: '13px' }}>
                     Choose from your events, trips, or profile photos
                  </Typography>
               </Box>
            )
         )}

         {/* Step 2: Analyze */}
         {currentStep === 2 && (
            <StepAnalyze isAnalyzing={isAnalyzing} />
         )}

         {/* Step 3: Review */}
         {currentStep === 3 && analysis && (
            <>
               {/* Photo preview */}
               <Box sx={{
                  width: '100%',
                  height: { xs: 200, sm: 240 },
                  overflow: 'hidden',
                  borderRadius: '8px',
                  border: '1px solid #253530',
                  mb: 2,
                  background: '#0A0F0C',
               }}>
                  <img
                     src={selectedPhoto?.url}
                     alt="Photo to enhance"
                     style={{
                        width: '100%', height: '100%',
                        objectFit: 'contain', imageOrientation: 'from-image',
                     }}
                  />
               </Box>
               <StepReview
                  analysis={analysis}
                  settings={settings}
                  onSettingsChange={handleSettingsChange}
                  onReset={handleReset}
                  onEnhance={handleEnhance}
                  onBack={handleBack}
                  isModified={isModified}
               />
            </>
         )}

         {/* Step 4: Enhance */}
         {currentStep === 4 && (
            <StepEnhance isEnhancing={isEnhancing} />
         )}

         {/* Step 5: Save */}
         {currentStep === 5 && selectedPhoto && enhancedImageUrl && (
            <StepSave
               originalUrl={selectedPhoto.url}
               enhancedUrl={enhancedImageUrl}
               onSave={handleSave}
               onReEnhance={handleReEnhance}
               onNewPhoto={handleNewPhoto}
            />
         )}

         <Snackbar
            open={snackOpen}
            autoHideDuration={5000}
            onClose={() => setSnackOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity}>
               {snackMsg}
            </Alert>
         </Snackbar>
      </Paper>
   );
};

export default TransformationPanel;
